import { AuthState, LocalStorageKey, clientServicesConfig } from '@audacy-clients/client-services';
import { Environment, Platform, PlayerAction } from '@audacy-clients/client-services/core';
import { createUuid, sha256 } from '@audacy-clients/client-services/src/Utilities';
import type { IAnalyticsEvent } from '@audacy-clients/client-services/src/analyticServices';
import type { IPlayer } from '@audacy-clients/client-services/src/audioServices/players/types';
import type { ILogger } from '@audacy-clients/client-services/src/logger';
import {
  AdTargetingStatusParams,
  AdTargetingStatusResponse,
} from '@audacy-clients/client-services/src/personalizationServices/types';
import { featureFlagMap } from '@audacy-clients/client-services/src/utils';
import { HtmlDataStore, HtmlPlayer, NativeHlsPlayer } from '@audacy-clients/client-services/web';
import { adTargetingState } from '@audacy-clients/core/atoms/adTargeting';
import type { IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';
import {
  getBrazeFeatureFlags,
  initializeBrazeFeatureFlagsService,
  sendEventsToBraze,
  sharedBrazeFeatureFlagsService,
} from '@audacy-clients/core/utils/braze';
import clientServices, {
  getClientServices,
  type IAppInfo,
  type IClientServicesConfig,
  initializeClientServices,
  type IPlatformSpecificConfig,
  type TSafeClientServices,
} from '@audacy-clients/core/utils/clientServices';
import { setRecoil } from '@audacy-clients/core/utils/recoil';
import * as braze from '@braze/web-sdk';
import { Logger } from 'tslog';
import { sendEventsToBranch } from '~/utils/branch';
import { addBrazeEventListener, disableBraze } from '~/utils/braze';
import { isMobileIOSDevice } from '~/utils/browser';
import { env } from '~/utils/env';
import { sendEventsToFacebook } from '~/utils/facebook';
import { setGoogleAdPreferences } from '~/utils/googleAds';
import type { AuthWithDeviceId, BrazeOptions } from './types';

let hasInit = false;
const isSafariMobile = isMobileIOSDevice();

const appInfo: IAppInfo = {
  appBuildNumber: '',
  appName: 'Audacy',
  appVersion: env.APP_VERSION ?? '',
};

const config: IClientServicesConfig = {
  env: env.API_ENV ? Environment[env.API_ENV] : Environment.PROD,
  platform: Platform.WEB,
  webcastMetricsStr: 'DIST=CBS&TGT=radiocomDev&SRS=CBS',
};

// used by browsers and extensions that send a GPC signal https://globalprivacycontrol.github.io/gpc-spec/
type IGPCNavigator = {
  globalPrivacyControl?: boolean;
} & Navigator;

const minLevel = Number(env.LOG_LEVEL) || 4;
const rootLogger = new Logger({ minLevel, hideLogPositionForProduction: true });

const loggerFrom = (l: typeof rootLogger): ILogger => ({
  debug: l.debug.bind(l),
  error: l.error.bind(l),
  info: l.info.bind(l),
  trace: l.trace.bind(l),
  warn: l.warn.bind(l),
  log: l.info.bind(l),
  getSubLogger(params) {
    return loggerFrom(l.getSubLogger(params));
  },
});

const logger = loggerFrom(rootLogger);

const getAudioPlayer = (featureFlags: IFeatureFlags): IPlayer => {
  let audioPlayer: IPlayer;

  // injecting different players based on whether the browser supports Media Source Extensions or native playback will give
  // us great ability to debug errors between platforms and develop platform specifici solutions without affecting each other.
  // At the moment this is just iphone Safari which does not support Media Source Extensions, and all other supported browsers.
  if (isSafariMobile) {
    audioPlayer = new NativeHlsPlayer({
      logger: logger.getSubLogger({ name: 'NativeHlsPlayer ' }),
      featureFlags,
    });
  } else {
    audioPlayer = new HtmlPlayer({
      logger: logger.getSubLogger({ name: 'HtmlPlayer ' }),
      featureFlags,
    });
  }

  return audioPlayer;
};

const getPlatformSpecific = (featureFlags: IFeatureFlags): IPlatformSpecificConfig => ({
  analyticsEventListener: (events: Array<IAnalyticsEvent>): void => {
    // filter out CONTINUE_LISTEN in favor of CONTINUE_LISTEN_10
    const filteredEvents = events.filter((event) => event.type !== PlayerAction.CONTINUE_LISTEN);

    if (!disableBraze) {
      try {
        sendEventsToBraze(braze.logCustomEvent, filteredEvents, appInfo);
      } catch (_e) {
        // catch error in case of broken braze campaign/data
      }
    }

    sendEventsToBranch(filteredEvents);
    sendEventsToFacebook(filteredEvents);
  },
  dataStore:
    typeof document !== 'undefined'
      ? new HtmlDataStore()
      : {
          clearData: async (): Promise<void> => {
            // empty
          },
          getData: async (): Promise<undefined> => undefined,
          getDataSync: (): undefined => undefined,
          setData: async (): Promise<void> => {
            // empty
          },
          setDataSync: (): void => {
            // empty
          },
        },
  featureFlags,
  player: getAudioPlayer(featureFlags),
  logger,
  callBrazeEvent: braze.logCustomEvent.bind(braze),
  useMp3RewindStreamer: env.USE_MP3_REWIND_STREAMER,
});

export async function initClientServices(): Promise<TSafeClientServices> {
  if (hasInit) {
    return getClientServices();
  }
  hasInit = true;

  featureFlagMap.set(featureFlagMap.brazeFlags, await getBrazeFeatureFlags());

  const flags = featureFlagMap.getAll();

  initializeClientServices(appInfo, config, getPlatformSpecific(flags));
  return getClientServices();
}

const getAuthStateFromDataStore = (): AuthWithDeviceId => {
  const dataStore = new HtmlDataStore();
  const refreshToken = dataStore.getDataSync<string>(LocalStorageKey.REFRESH_TOKEN);
  const userToken = dataStore.getDataSync<string>(LocalStorageKey.USER_TOKEN);
  const deviceId = dataStore.getDataSync<string>(LocalStorageKey.BRAZE_DEVICE_ID);

  if (refreshToken && userToken) {
    return {
      state: AuthState.AUTH,
      deviceId: deviceId,
    };
  }

  if (userToken) {
    return {
      state: AuthState.ANON,
      deviceId: userToken,
    };
  }

  const f8kValue = `f8k:${sha256(createUuid()).substring(3, 35)}`;
  dataStore.setData(LocalStorageKey.BRAZE_DEVICE_ID, f8kValue);

  return {
    state: AuthState.NONE,
    deviceId: f8kValue,
  };
};

export const initBraze = (): void => {
  if (disableBraze) {
    console.warn(
      `Braze disabled, if this was a mistake remove the DISABLE_BRAZE var from your config. env.DISABLE_BRAZE: ${env.DISABLE_BRAZE}`,
    );

    return;
  }

  const options: BrazeOptions = {};
  const { deviceId } = getAuthStateFromDataStore();
  options.deviceId = deviceId;
  braze.initialize(env.BRAZE_API_KEY, {
    baseUrl: env.BRAZE_SDK_ENDPOINT,
    allowUserSuppliedJavascript: true,
    minimumIntervalBetweenTriggerActionsInSeconds: 1,
    ...options,
  });
  braze.automaticallyShowInAppMessages();
  braze.openSession();
  if (deviceId) {
    braze.getUser()?.addAlias('USER_ALIAS', deviceId);
  }

  // listens for events sent from braze iframes
  addBrazeEventListener();

  initializeBrazeFeatureFlagsService(braze);

  // Set the max unauthenticated playback duration in local storage if the 'meteredContent' feature flag is active.
  sharedBrazeFeatureFlagsService
    .getFeatureFlag('meteredContent')
    .then((meteredContentBrazeFeatureFlag) => {
      getClientServices().then((cs) => {
        if (
          meteredContentBrazeFeatureFlag?.properties?.active &&
          meteredContentBrazeFeatureFlag.properties?.duration?.value !== undefined
        ) {
          cs.getPersonalizationServices().dataStore.setDataSync(
            LocalStorageKey.UNAUTHENTICATED_PLAYBACK_MAX_DURATION,
            meteredContentBrazeFeatureFlag.properties.duration.value,
          );
        } else {
          cs.getPersonalizationServices().dataStore.clearData(
            LocalStorageKey.UNAUTHENTICATED_PLAYBACK_MAX_DURATION,
          );
        }
      });
    });
};

// gets ad preferences for authenticated users
export const syncAdPreferences = async (): Promise<void> => {
  const response = await clientServices.getPersonalizationServices().getAdTargetingStatus();

  if (response?.adTargeting === AdTargetingStatusResponse.INELIGIBLE) {
    clientServicesConfig.disableTargetedAds = true;
    setRecoil(adTargetingState, AdTargetingStatusResponse.INELIGIBLE);
    // Google privacy settings for INELIGIBLE should be same as OPT_OUT
    setGoogleAdPreferences(AdTargetingStatusParams.OPT_OUT);
  } else if (
    response?.adTargeting === AdTargetingStatusResponse.OPT_OUT ||
    (navigator as IGPCNavigator).globalPrivacyControl // globalPrivacyControl setting comes from a browser or plugin
  ) {
    clientServicesConfig.disableTargetedAds = true;
    setRecoil(adTargetingState, AdTargetingStatusResponse.OPT_OUT);
    setGoogleAdPreferences(AdTargetingStatusParams.OPT_OUT);
  } else {
    clientServicesConfig.disableTargetedAds = false;
    setRecoil(adTargetingState, AdTargetingStatusResponse.OPT_IN);
    setGoogleAdPreferences(AdTargetingStatusParams.OPT_IN);
  }
};

export const initAdPreferences = async (): Promise<void> => {
  const authState = clientServices.getPersonalizationServices().getAuthState();

  if (authState === AuthState.AUTH) {
    await syncAdPreferences();
  }
  // Unauthenticated consent is managed via DataGrail/GTM
};
